import React, { useEffect, useRef, useState } from 'react';
import '../styles/MainPage.css';

export default function MainPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Site key proporcionada por el usuario. Puede ser sobrescrita por la env var `REACT_APP_RECAPTCHA_SITE_KEY`.
  const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LdNs0osAAAAABTJxxTgR8P65xkiD1q9PRzWEDPs';
  const slidesCount = 4;
  const slideIntervalRef = useRef(null);
  const cardsObserverRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    function next() { setCurrentSlide(s => (s + 1) % slidesCount); }
    slideIntervalRef.current = setInterval(next, 5000);
    return () => clearInterval(slideIntervalRef.current);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Intersection Observer for animation
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('animate-slide-in'); });
    }, { threshold: 0.1 });

    const cards = root.querySelectorAll('.card-hover');
    cards.forEach(c => observer.observe(c));
    cardsObserverRef.current = observer;

    // Set current year
    const yearEl = root.querySelector('#current-year');
    if (yearEl) yearEl.textContent = new Date().getFullYear();

    // Element SDK integration if present
    const defaultConfig = {
      company_name: 'DOS VÍAS',
      hero_title: 'Servicios Industriales',
      contact_email: 'contacto@dosvias.cl',
      contact_website: 'www.dosvias.cl'
    };
    if (window.elementSdk && typeof window.elementSdk.init === 'function') {
      window.elementSdk.init({
        defaultConfig,
        onConfigChange: (config) => {
          const companyName = config.company_name || defaultConfig.company_name;
          const contactEmail = config.contact_email || defaultConfig.contact_email;
          const contactWebsite = config.contact_website || defaultConfig.contact_website;
          const navName = root.querySelector('#nav-company-name');
          const footerName = root.querySelector('#footer-company-name');
          const contactEmailEl = root.querySelector('#contact-email');
          const contactWebsiteEl = root.querySelector('#contact-website');
          const footerEmail = root.querySelector('#footer-email');
          const footerWebsite = root.querySelector('#footer-website');
          if (navName) navName.textContent = companyName;
          if (footerName) footerName.textContent = companyName;
          if (contactEmailEl) contactEmailEl.textContent = contactEmail;
          if (contactWebsiteEl) contactWebsiteEl.textContent = contactWebsite;
          if (footerEmail) footerEmail.textContent = contactEmail;
          if (footerWebsite) footerWebsite.textContent = contactWebsite;
        }
      });
    }

    return () => {
      if (cardsObserverRef.current) cardsObserverRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    // reset interval when user manually changes slide
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = setInterval(() => setCurrentSlide(s => (s + 1) % slidesCount), 5000);
    }
  }, [currentSlide]);

  function goToSlide(i) {
    setCurrentSlide(((i % slidesCount) + slidesCount) % slidesCount);
  }

  function handleNavClick(e, selector) {
    e.preventDefault();
    const el = document.querySelector(selector);
    if (!el) return;
    const headerOffset = 80;
    const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    setMobileOpen(false);
  }

  useEffect(() => {
    if (!RECAPTCHA_KEY) return;
    if (window.grecaptcha) return; // already loaded
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch (e) {} };
  }, [RECAPTCHA_KEY]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setFormMessage(null);
    setSubmitting(true);

    try {
      const fd = new FormData(e.target);
      const nombre = (fd.get('nombre') || '').toString().trim();
      const empresa = (fd.get('empresa') || '').toString().trim();
      const email = (fd.get('email') || '').toString().trim();
      const telefono = (fd.get('telefono') || '').toString().trim();
      const mensaje = (fd.get('mensaje') || '').toString().trim();

      if (!nombre || nombre.length < 2) throw new Error('Nombre inválido');
      if (!email) throw new Error('Email inválido');
      if (!mensaje || mensaje.length < 10) throw new Error('Mensaje muy corto');

      // obtain reCAPTCHA token (v3)
      let captchaToken = '';
      if (RECAPTCHA_KEY && window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
        await window.grecaptcha.ready();
        // action must match backend expectation ('contact')
        captchaToken = await window.grecaptcha.execute(RECAPTCHA_KEY, { action: 'contact' });
      }

      if (!captchaToken) {
        // still send empty token to let backend respond, but inform user
        console.warn('No se obtuvo token de reCAPTCHA');
      }

      const payload = {
        name: nombre,
        company: empresa,
        email,
        phone: telefono,
        message: mensaje,
        captchaToken
      };

      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data && data.error ? data.error : 'Error al enviar el formulario');
      }

      setFormMessage({ type: 'success', text: `¡Mensaje enviado! Gracias ${nombre}, nos pondremos en contacto pronto.` });
      e.target.reset();
      // clear after some time
      setTimeout(() => setFormMessage(null), 6000);
    } catch (err) {
      console.error('Error enviando contacto:', err);
      setFormMessage({ type: 'error', text: err.message || 'No se pudo enviar el mensaje' });
    } finally {
      setSubmitting(false);
    }
  }

  const valves = [
    { img: '/valvuladebola.png', title: 'Válvulas de Bola', desc: 'Control de flujo on/off de alta eficiencia' },
    { img: '/valvulamariposa.png', title: 'Válvulas Mariposa', desc: 'Regulación de grandes caudales' },
    { img: '/valvuladecompuerta.png', title: 'Válvulas de Compuerta', desc: 'Aislamiento total de líneas' },
    { img: '/valvuladeglobo.png', title: 'Válvulas de Globo', desc: 'Regulación precisa de flujo' },
    { img: '/valvuladeretencion.png', title: 'Válvulas de Retención', desc: 'Prevención de reflujo' },
    { img: '/valvuladecuchillo.png', title: 'Válvulas de Cuchillo', desc: 'Para fluidos con sólidos' },
    { img: '/valvuladecono.png', title: 'Válvulas de Cono', desc: 'Control de alta precisión' },
    { img: '/valvuladediafragma.png', title: 'Válvulas de Diafragma', desc: 'Para fluidos corrosivos' },
    { img: '/valvuladepinch.png', title: 'Válvulas Pinch', desc: 'Control de pulpas y lodos' },
    { img: '/valvuladecontrol.png', title: 'Válvulas de Control', desc: 'Regulación automatizada' },
    { img: '/valvulaplastica.png', title: 'Válvulas Plásticas', desc: 'Resistencia química' }
  ];

  const automationItems = [
    ['Actuadores Neumáticos','Accionamiento por aire comprimido'],
    ['Actuadores Eléctricos','Control preciso motorizado'],
    ['Actuadores Hidráulicos','Alta fuerza y torque'],
    ['Válvulas Solenoides','Control electromagnético'],
    ['Posicionadores','Regulación de posición'],
    ['Limit Switch','Indicadores de posición']
  ];

  const instrumentationItems = [
    ['Medidores de Caudal','Flujo volumétrico y másico'],
    ['Manómetros','Medición de presión'],
    ['Transmisores de Presión','Señales analógicas 4-20mA'],
    ['Medidores de Nivel','Tanques y depósitos'],
    ['Interruptores de Nivel','Alarmas y control'],
    ['Instrumentos de Flujo','Control de procesos']
  ];

  // Partner logos (placeholders — replace with your uploaded images)
  const partnerLogos = [
    'logo1.png',
    'logo2.png',
    'logo3.png',
    'logo4.png',
    'logo5.png',
    'logo6.png',
    'logo7.png',
    'logo8.png',
    'logo9.png',
    'logo10.png',
    'logo11.png',
    'logo12.png',
    'logo13.png',
    'logo14.png',
    'logo15.png',
    'logo16.png',
    'logo17.png',
    'logo18.png',
    'logo19.png',
    'logo20.png',
    "logo21.png",
    "logo22.png",
    "logo23.png",
    "logo24.png"
    
  ];
  const partnerLogosReversed = [...partnerLogos].slice().reverse();
  const topTrackRef = useRef(null);
  const bottomTrackRef = useRef(null);

  useEffect(() => {
    function setupMarquee(track, direction = 'left', pxPerSec = 60) {
      if (!track) return null;
      const container = track.parentElement;
      track.style.willChange = 'transform';

      let offset = 0;
      let rafId = null;
      let lastTime = performance.now();

      function step(now) {
        const dt = now - lastTime;
        lastTime = now;
        const delta = (pxPerSec * dt) / 1000; // pixels to move this frame
        offset += direction === 'left' ? -delta : delta;
        track.style.transform = `translateX(${offset}px)`;

        const containerRect = container.getBoundingClientRect();

        if (direction === 'left') {
          const first = track.children[0];
          if (first) {
            const rect = first.getBoundingClientRect();
            if (rect.right <= containerRect.left + 1) {
              const style = getComputedStyle(first);
              const w = first.offsetWidth + (parseFloat(style.marginRight) || 0);
              track.appendChild(first);
              offset += w;
              track.style.transform = `translateX(${offset}px)`;
            }
          }
        } else {
          const last = track.children[track.children.length - 1];
          if (last) {
            const rect = last.getBoundingClientRect();
            if (rect.left >= containerRect.right - 1) {
              const style = getComputedStyle(last);
              const w = last.offsetWidth + (parseFloat(style.marginRight) || 0);
              track.insertBefore(last, track.children[0]);
              offset -= w;
              track.style.transform = `translateX(${offset}px)`;
            }
          }
        }

        rafId = requestAnimationFrame(step);
      }

      rafId = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafId);
    }

    const cleanupTop = setupMarquee(topTrackRef.current, 'left', 60);
    const cleanupBottom = setupMarquee(bottomTrackRef.current, 'right', 80);

    return () => {
      if (typeof cleanupTop === 'function') cleanupTop();
      if (typeof cleanupBottom === 'function') cleanupBottom();
    };
  }, []);
  
  // Product catalog data adapted from original HTML
  const productCatalog = {
    valvulas: {
      title: 'Válvulas Industriales',
      subtitle: 'Portafolio completo para control y aislamiento de fluidos',
      products: [
        { id: 'valvula-bola', name: 'Válvulas de Bola', shortDesc: 'Control on/off de alta eficiencia', description: 'Válvulas de bola de 2 y 3 vías para cierre hermético y operación rápida. Diseñadas para aplicaciones de alta presión y temperatura con mínima pérdida de carga.', features: ['Paso total o reducido','Acero carbono, inoxidable, bronce','Roscada, bridada, soldable','Presión hasta 3000 PSI','Operación manual o automatizada'], applications: ['Industria química','Minería','Oil & Gas','Plantas de procesos'], icon: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4" fill="currentColor"/><line x1="4" y1="12" x2="8" y2="12"/><line x1="16" y1="12" x2="20" y2="12"/>' },
        { id: 'valvula-mariposa', name: 'Válvulas Mariposa', shortDesc: 'Regulación de grandes caudales', description: 'Válvulas mariposa tipo wafer y lug para regulación y aislamiento en líneas de gran diámetro. Diseño compacto con bajo torque de operación.', features: ['Diámetros desde 2" hasta 48"','Doble excéntrica y triple offset','Body hierro dúctil o acero inoxidable','Revestimientos EPDM, NBR, PTFE','Actuadores neumáticos o eléctricos'], applications: ['Tratamiento de agua','HVAC','Procesos industriales','Sistemas de refrigeración'], icon: '<circle cx="12" cy="12" r="8"/><ellipse cx="12" cy="12" rx="2" ry="6" fill="currentColor"/><line x1="4" y1="12" x2="20" y2="12"/>' },
        { id: 'valvula-compuerta', name: 'Válvulas de Compuerta', shortDesc: 'Aislamiento total de líneas', description: 'Válvulas de compuerta para servicio de apertura/cierre completo. Mínima restricción al flujo en posición abierta, ideales para líneas principales.', features: ['Rising stem y non-rising stem','Sello metal-metal o resiliente','Clase 150 a 2500','Extremos bridados o roscados','Vástago ascendente/no ascendente'], applications: ['Distribución de agua','Sistemas de vapor','Petróleo y gas','Minería'], icon: '<rect x="6" y="4" width="12" height="16" rx="1"/><rect x="9" y="8" width="6" height="3" fill="currentColor"/><line x1="12" y1="4" x2="12" y2="1"/>' },
        { id: 'valvula-globo', name: 'Válvulas de Globo', shortDesc: 'Regulación precisa de flujo', description: 'Válvulas de globo para control preciso de caudal y estrangulamiento. Diseño que permite ajuste fino del flujo en servicios críticos.', features: ['Asiento recto, angular o en Y','Trim estándar o especial','Materiales resistentes a erosión','Alta precisión de control','Servicio hasta 540°C'], applications: ['Servicios de vapor','Condensado','Agua de alimentación','Procesos químicos'], icon: '<path d="M6 12 Q6 6 12 6 Q18 6 18 12 Q18 18 12 18 Q6 18 6 12"/><circle cx="12" cy="12" r="3" fill="currentColor"/>' },
        { id: 'valvula-retencion', name: 'Válvulas de Retención', shortDesc: 'Prevención de reflujo', description: 'Válvulas check para prevenir el retorno de flujo. Diseño automático que permite flujo en una sola dirección protegiendo equipos y procesos.', features: ['Tipo swing, lift, dual plate','Cierre silencioso','Mínima pérdida de carga','Instalación horizontal o vertical','Materiales anticorrosivos'], applications: ['Bombas centrífugas','Líneas de descarga','Sistemas de vapor','Agua potable'], icon: '<circle cx="12" cy="12" r="8"/><path d="M8 12 L16 12 M14 9 L17 12 L14 15" fill="none"/>' },
        { id: 'valvula-cuchillo', name: 'Válvulas de Cuchillo', shortDesc: 'Para fluidos con sólidos', description: 'Válvulas tipo cuchillo o guillotina para servicio en fluidos con alto contenido de sólidos en suspensión. Diseño robusto que corta materiales fibrosos.', features: ['Paso libre sin obstrucciones','Compuerta de acero inoxidable','Cierre bidireccional','Sellado resiliente','Diseño autolimpiante'], applications: ['Pulpa y papel','Minería (relaves)','Tratamiento de aguas residuales','Industria alimentaria'], icon: '<rect x="6" y="6" width="12" height="12" rx="1"/><path d="M12 2 L12 6" stroke-width="2"/>' },
        { id: 'valvula-cono', name: 'Válvulas de Cono', shortDesc: 'Control de alta precisión', description: 'Válvulas de cono rotativo para regulación precisa en servicios severos. Tecnología de última generación para control exacto con mínima cavitación.', features: ['Cono cromado endurecido','Zero leakage (clase VI)','Rangeabilidad 300:1','Servicio hasta 425°C','Bajo ruido operacional'], applications: ['Refinación','Petroquímica','Generación eléctrica','Servicios criogénicos'], icon: '<circle cx="12" cy="12" r="8"/><path d="M8 16 L12 8 L16 16 Z" fill="currentColor"/>' },
        { id: 'valvula-diafragma', name: 'Válvulas de Diafragma', shortDesc: 'Para fluidos corrosivos', description: 'Válvulas de diafragma para servicios altamente corrosivos y abrasivos. Sello hermético mediante membrana flexible que aísla el mecanismo del fluido.', features: ['Diafragma EPDM, PTFE, Viton','Cuerpo revestido PFA, PTFE','Diseño weir o straight-through','Operación manual o neumática','Zero contaminación'], applications: ['Ácidos y álcalis','Farmacéutica','Alimentos y bebidas','Tratamiento químico'], icon: '<rect x="5" y="8" width="14" height="8" rx="1"/><path d="M5 12 Q12 6 19 12" stroke-width="2"/>' },
        { id: 'valvula-pinch', name: 'Válvulas Pinch', shortDesc: 'Control de pulpas y lodos', description: 'Válvulas de pellizco con manga elastomérica para manejo de sólidos en suspensión. El único componente en contacto con el medio es la manga reemplazable.', features: ['Manga de goma natural o sintética','Paso completo sin restricciones','Bajo costo de mantenimiento','Cierre hermético','Resistencia a abrasión'], applications: ['Minería (concentrados)','Lodos industriales','Pulpas de minerales','Cemento'], icon: '<path d="M4 12 Q8 8 12 12 Q16 16 20 12"/><path d="M4 12 Q8 16 12 12 Q16 8 20 12"/><circle cx="12" cy="12" r="2" fill="currentColor"/>' },
        { id: 'valvula-control', name: 'Válvulas de Control', shortDesc: 'Regulación automatizada', description: 'Válvulas de control automático para regulación continua de caudal, presión o nivel. Integración con sistemas DCS/PLC para control preciso de procesos.', features: ['Posicionador digital','Señal 4-20mA, HART, Profibus','Cv desde 0.01 hasta 5000','Trim anticavitación','Certificaciones SIL'], applications: ['Plantas de procesos','Refinación','Generación de energía','Control de temperatura'], icon: '<rect x="6" y="10" width="12" height="8" rx="1"/><rect x="8" y="4" width="8" height="6" rx="1"/><circle cx="12" cy="7" r="1.5" fill="currentColor"/>' },
        { id: 'valvula-plastica', name: 'Válvulas Plásticas', shortDesc: 'Resistencia química superior', description: 'Válvulas construidas en termoplásticos de ingeniería (PVC, CPVC, PP, PVDF) para máxima resistencia química. Livianas, económicas y libres de corrosión.', features: ['PVC, CPVC, PP, PVDF','Válvulas de bola, mariposa, check','Roscadas, bridadas, soldables','Temperatura hasta 140°C','Certificaciones NSF, FDA'], applications: ['Tratamiento químico','Piscinas y spa','Galvanoplastia','Industria alimentaria'], icon: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4" stroke-dasharray="2 2"/><circle cx="12" cy="12" r="2" fill="currentColor"/>' }
      ]
    },
    canerias: {
      title: 'Cañerías y Fittings',
      subtitle: 'Sistemas completos de conducción de fluidos',
      products: [
        {
          id: 'caneria-acero-carbono',
          name: 'Cañería Acero Carbono',
          shortDesc: 'Para aplicaciones generales',
          description: 'Tubería de acero al carbono según normas ASTM para conducción de fluidos en aplicaciones industriales. Disponible en diferentes schedules y diámetros.',
          features: ['ASTM A53, A106, API 5L', 'Schedule 40, 80, 160, XXS', 'Diámetros 1/2" a 48"', 'Sin costura o con costura', 'Extremos lisos, biselados o roscados'],
          applications: ['Vapor', 'Agua', 'Aire comprimido', 'Oil & Gas'],
          icon: '<path d="M2 12 L22 12"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="12" r="3"/>'
        },
        {
          id: 'caneria-inox',
          name: 'Cañería Acero Inoxidable',
          shortDesc: 'Resistencia a corrosión',
          description: 'Tubería de acero inoxidable AISI 304, 316, 316L para servicios corrosivos y sanitarios. Excelente resistencia mecánica y durabilidad.',
          features: ['AISI 304, 316, 316L, 321', 'Acabado sanitario o industrial', 'Sin costura o soldada', 'Schedule 5S a 80S', 'Certificados de material 3.1'],
          applications: ['Industria alimentaria', 'Farmacéutica', 'Química', 'Marina'],
          icon: '<path d="M2 12 L22 12"/><rect x="5" y="10" width="4" height="4" rx="0.5"/><rect x="15" y="10" width="4" height="4" rx="0.5"/>'
        },
        {
          id: 'codos',
          name: 'Codos',
          shortDesc: 'Cambios de dirección',
          description: 'Codos de 45° y 90° en diversos materiales para cambios de dirección en sistemas de tuberías. Radio largo y radio corto según aplicación.',
          features: ['45°, 90°, 180°', 'Radio largo (LR) y corto (SR)', 'Buttweld o roscados', 'Acero carbono, inoxidable', 'Clase 150 a 3000'],
          applications: ['Todas las industrias', 'Sistemas de tuberías', 'Plantas de proceso'],
          icon: '<path d="M4 16 L4 12 Q4 8 8 8 L12 8" stroke-width="2" fill="none"/>'
        },
        {
          id: 'tees',
          name: 'Tees',
          shortDesc: 'Derivaciones',
          description: 'Tees rectas y reductoras para derivaciones en sistemas de tuberías. Diseño que minimiza turbulencias y pérdida de carga.',
          features: ['Tee recta y reductora', 'Buttweld, roscada, socket weld', 'Acero carbono, inoxidable, aleaciones', 'Reforzada para alta presión', 'Según ASME B16.9'],
          applications: ['Sistemas de distribución', 'Cabezales', 'Líneas de proceso'],
          icon: '<path d="M12 4 L12 12 M4 12 L20 12" stroke-width="2.5" stroke-linecap="round"/>'
        },
        {
          id: 'reducciones',
          name: 'Reducciones',
          shortDesc: 'Cambios de diámetro',
          description: 'Reducciones concéntricas y excéntricas para transiciones entre diferentes diámetros de tubería. Diseño optimizado para flujo laminar.',
          features: ['Concéntrica y excéntrica', 'Buttweld según ASME B16.9', 'Todos los materiales', 'Todas las combinaciones de diámetros', 'Acabado mecanizado'],
          applications: ['Succión de bombas', 'Líneas de proceso', 'Sistemas de vapor'],
          icon: '<path d="M8 8 L16 12 L8 16 Z" fill="none" stroke="currentColor" stroke-width="2"/>'
        },
        {
          id: 'flanges',
          name: 'Flanges',
          shortDesc: 'Conexiones bridadas',
          description: 'Bridas en diversos tipos y clases de presión para uniones desmontables. Facilitan mantenimiento y cambio de equipos.',
          features: ['Slip-on, weld neck, blind, lap joint', 'Clase 150 a 2500', 'ASME B16.5, B16.47', 'Face RF, FF, RTJ', 'Acero carbono, inoxidable, aleaciones'],
          applications: ['Válvulas', 'Equipos', 'Tuberías principales'],
          icon: '<circle cx="12" cy="12" r="8" stroke-width="2" fill="none"/><circle cx="12" cy="12" r="4" fill="currentColor"/>'
        }
      ]
    },
    automatizacion: {
      title: 'Automatización',
      subtitle: 'Soluciones de control y actuación',
      products: [
        {
          id: 'actuador-neumatico',
          name: 'Actuadores Neumáticos',
          shortDesc: 'Accionamiento por aire',
          description: 'Actuadores neumáticos de simple y doble efecto para automatización de válvulas. Torques desde 5 hasta 16,000 Nm. Diseño robusto para ambientes industriales.',
          features: ['Simple efecto con resorte', 'Doble efecto', 'Torque 5 a 16,000 Nm', 'Aluminio, acero inoxidable', 'Certificaciones ATEX, SIL'],
          applications: ['Válvulas de bola', 'Válvulas mariposa', 'Procesos automatizados'],
          icon: '<rect x="6" y="8" width="12" height="10" rx="2"/><path d="M9 8 L9 4 L15 4 L15 8"/>'
        },
        {
          id: 'actuador-electrico',
          name: 'Actuadores Eléctricos',
          shortDesc: 'Control preciso motorizado',
          description: 'Actuadores eléctricos multivuelta y cuarto de vuelta con control posicional exacto. Integración con sistemas de control industrial.',
          features: ['220V, 380V, 24VDC', 'Torque 10 a 4,000 Nm', 'Control on/off o modulante', 'Display local y remoto', 'Comunicación Modbus, Profibus'],
          applications: ['Válvulas de compuerta', 'Válvulas de control', 'Sistemas SCADA'],
          icon: '<rect x="6" y="6" width="12" height="14" rx="2"/><path d="M10 2 L10 6"/>'
        },
        {
          id: 'posicionador',
          name: 'Posicionadores',
          shortDesc: 'Control de posición',
          description: 'Posicionadores electroneumáticos para válvulas de control. Convierte señal eléctrica en presión neumática proporcional para posicionamiento exacto.',
          features: ['Señal 4-20mA, HART', 'Auto-calibración', 'Diagnóstico predictivo', 'Comunicación digital', 'Certificación SIL 2/3'],
          applications: ['Válvulas de control', 'Lazos de control PID', 'Plantas automatizadas'],
          icon: '<rect x="5" y="5" width="14" height="14" rx="2"/><circle cx="12" cy="12" r="4"/>'
        },
        {
          id: 'solenoide',
          name: 'Válvulas Solenoides',
          shortDesc: 'Control electromagnético',
          description: 'Electroválvulas para control de aire, agua, gas, vapor. Operación rápida y confiable mediante bobina electromagnética.',
          features: ['2/2, 3/2, 5/2, 5/3 vías', '24VDC, 110VAC, 220VAC', 'Normalmente abierta/cerrada', 'Prueba de explosión', 'Conexión ISO, NPT, G'],
          applications: ['Control neumático', 'Sistemas de seguridad', 'Procesos automatizados'],
          icon: '<rect x="7" y="8" width="10" height="10" rx="1"/>'
        }
      ]
    },
    instrumentacion: {
      title: 'Instrumentación',
      subtitle: 'Medición y monitoreo de procesos',
      products: [
        {
          id: 'caudalimetro',
          name: 'Medidores de Caudal',
          shortDesc: 'Medición de flujo',
          description: 'Caudalímetros electromagnéticos, vortex, ultrasónicos y másicos para medición precisa de caudal en líquidos y gases.',
          features: ['Electromagnético, vortex, másico', 'Precisión ±0.5%', 'Señal 4-20mA, pulso, HART', 'Display local', 'Certificación ATEX'],
          applications: ['Facturación', 'Control de procesos', 'Balance de materiales'],
          icon: '<circle cx="12" cy="12" r="8"/><path d="M12 6 L12 12 L16 14"/>'
        },
        {
          id: 'manometro',
          name: 'Manómetros',
          shortDesc: 'Indicación de presión',
          description: 'Manómetros analógicos y digitales para medición e indicación local de presión. Construcción robusta para ambientes industriales.',
          features: ['Glicerina o seco', 'Diámetro 2.5", 4", 6"', 'Acero inoxidable 316', 'Clase 1.6, 2.5', 'Conexión radial o axial'],
          applications: ['Tableros', 'Líneas de proceso', 'Compresores'],
          icon: '<circle cx="12" cy="12" r="8"/><path d="M12 12 L12 7"/>'
        },
        {
          id: 'transmisor-presion',
          name: 'Transmisores de Presión',
          shortDesc: 'Señal analógica',
          description: 'Transmisores de presión absoluta, manométrica y diferencial con salida 4-20mA. Tecnología piezorresistiva de alta estabilidad.',
          features: ['4-20mA, HART, Profibus', 'Rango 0-1 bar a 0-1000 bar', 'Exactitud ±0.075%', 'Compensación de temperatura', 'Certificación SIL'],
          applications: ['Procesos críticos', 'Sistemas SCADA', 'Seguridad de procesos'],
          icon: '<rect x="6" y="4" width="12" height="16" rx="2"/><circle cx="12" cy="10" r="3"/>'
        },
        {
          id: 'nivel',
          name: 'Medidores de Nivel',
          shortDesc: 'Control de tanques',
          description: 'Instrumentos de nivel por radar, ultrasónico, flotador magnético y presión hidrostática. Soluciones para todo tipo de aplicaciones.',
          features: ['Radar, ultrasónico, magnético', 'Líquidos y sólidos', 'Salida 4-20mA, relé', 'Display local', 'Certificación ATEX'],
          applications: ['Tanques de almacenamiento', 'Silos', 'Reactores'],
          icon: '<rect x="6" y="4" width="12" height="16" rx="1"/><rect x="8" y="10" width="8" height="8" fill="currentColor" opacity="0.5"/>'
        }
      ]
    }
  };

  // Modal state for catalog and product detail
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogType, setCatalogType] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);

  function showProductCatalog(type) {
    if (!productCatalog[type]) return;
    setCatalogType(type);
    setCurrentProduct(null);
    setCatalogOpen(true);
    document.body.style.overflow = 'hidden';
  }

  function closeCatalog() {
    setCatalogOpen(false);
    setCatalogType(null);
    document.body.style.overflow = 'auto';
  }

  function showProductDetail(type, id) {
    const catalog = productCatalog[type];
    if (!catalog) return;
    const prod = catalog.products.find(p => p.id === id);
    if (!prod) return;
    setCurrentProduct(prod);
  }

  function closeProductDetail() {
    setCurrentProduct(null);
  }

  return (
    <div ref={rootRef} className="font-body bg-white text-gray-800 overflow-auto">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img src="/logo192.png" alt="DOS VÍAS" className="logo-icon" />
            

            <div className="hidden lg:flex items-center space-x-8">
              <a href="#inicio" onClick={(e) => handleNavClick(e, '#inicio')} className="nav-link font-medium text-[#3e484a] hover:text-[#0190d4]">Inicio</a>
              <a href="#valvulas" onClick={(e) => handleNavClick(e, '#valvulas')} className="nav-link font-medium text-[#3e484a] hover:text-[#0190d4]">Válvulas</a>
              <a href="#canerias" onClick={(e) => handleNavClick(e, '#canerias')} className="nav-link font-medium text-[#3e484a] hover:text-[#0190d4]">Cañerías y Fittings</a>
              <a href="#automatizacion" onClick={(e) => handleNavClick(e, '#automatizacion')} className="nav-link font-medium text-[#3e484a] hover:text-[#0190d4]">Automatización</a>
              <a href="#instrumentacion" onClick={(e) => handleNavClick(e, '#instrumentacion')} className="nav-link font-medium text-[#3e484a] hover:text-[#0190d4]">Instrumentación</a>
              <button onClick={(e) => handleNavClick(e, '#contacto')} className="btn-primary px-6 py-2.5 rounded-lg text-white font-semibold">Contacto</button>
            </div>

            <button id="mobile-menu-btn" onClick={() => setMobileOpen(o => !o)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <svg className="w-6 h-6 text-[#3e484a]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
        </div>

        <div id="mobile-menu" className={`mobile-menu fixed top-20 left-0 w-full h-full bg-white lg:hidden ${mobileOpen ? 'open' : ''}`}>
          <div className="px-4 py-6 space-y-4">
            <a href="#inicio" onClick={(e) => handleNavClick(e, '#inicio')} className="block py-3 px-4 text-lg font-medium text-[#3e484a] hover:bg-gray-50 rounded-lg">Inicio</a>
            <a href="#valvulas" onClick={(e) => handleNavClick(e, '#valvulas')} className="block py-3 px-4 text-lg font-medium text-[#3e484a] hover:bg-gray-50 rounded-lg">Válvulas</a>
            <a href="#canerias" onClick={(e) => handleNavClick(e, '#canerias')} className="block py-3 px-4 text-lg font-medium text-[#3e484a] hover:bg-gray-50 rounded-lg">Cañerías y Fittings</a>
            <a href="#automatizacion" onClick={(e) => handleNavClick(e, '#automatizacion')} className="block py-3 px-4 text-lg font-medium text-[#3e484a] hover:bg-gray-50 rounded-lg">Automatización</a>
            <a href="#instrumentacion" onClick={(e) => handleNavClick(e, '#instrumentacion')} className="block py-3 px-4 text-lg font-medium text-[#3e484a] hover:bg-gray-50 rounded-lg">Instrumentación</a>
            <a href="#contacto" onClick={(e) => handleNavClick(e, '#contacto')} className="block py-3 px-4 text-lg font-semibold text-white bg-[#0190d4] rounded-lg text-center">Contacto</a>
          </div>
        </div>
      </nav>

      <main>
        <section id="inicio" className="pt-20">
          <div className="slider-container h-[500px] md:h-[600px] relative">
            {[0,1,2,3].map(i => (
              <div key={i} className={`slide ${i === currentSlide ? 'active' : ''}`} data-slide={i}>
                {/* Background layers similar to original HTML for each slide */}
                {i === 0 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#3e484a] via-[#4a5658] to-[#3e484a]">
                    <div className="absolute inset-0 opacity-20">
                      
                      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none"><defs>
                        
                        <pattern id="grid1" width="10" height="10" patternUnits="userSpaceOnUse">
                          
                          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(1,144,212,0.3)" strokeWidth="0.5" />
                        </pattern>
                      </defs>
                      <rect width="100" height="100" fill="url(#grid1)" />
                      </svg>
                    </div>
                    
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-full opacity-30">
                    
                      <svg viewBox="0 0 200 200" className="w-full h-full"><circle cx="100" cy="100" r="80" fill="none" stroke="#0190d4" strokeWidth="2" /> <circle cx="100" cy="100" r="60" fill="none" stroke="#0190d4" strokeWidth="1.5" /> <circle cx="100" cy="100" r="40" fill="none" stroke="#0190d4" strokeWidth="1" /> <path d="M100 20 L100 180 M20 100 L180 100" stroke="#0190d4" strokeWidth="1" /> <rect x="70" y="70" width="60" height="60" fill="none" stroke="#0190d4" strokeWidth="2" rx="5" />
                      </svg>
                    </div>
                    <div className="slider-image-container">
                      <img src="/valvuladebola.png" alt="Válvula de bola" className="slider-image" />

                    </div>
                  </div>
                )}

                {i === 1 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0190d4] via-[#017bb5] to-[#016a9c]">
                    <div className="absolute inset-0 opacity-20">
                      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none"><defs>
                        <pattern id="pipes" width="20" height="20" patternUnits="userSpaceOnUse">
                          <circle cx="10" cy="10" r="3" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
                          <line x1="0" y1="10" x2="7" y2="10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                          <line x1="13" y1="10" x2="20" y2="10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                        </pattern>
                      </defs>
                      <rect width="100" height="100" fill="url(#pipes)" />
                      </svg>
                    </div>
                    <div className="slider-image-container">
                      <img src="/caneriasnofondo.png" alt="Canerías sin fondo en slider" className="slider-image" />

                    </div>
                  </div>
                )}

                {i === 2 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2c3e50] via-[#34495e] to-[#2c3e50]">
                    <div className="absolute inset-0 opacity-30">
                      <svg className="w-full h-full" viewBox="0 0 200 200"><defs>
                        <pattern id="circuit" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M0 20 L15 20 L20 15 L20 0" fill="none" stroke="#0190d4" strokeWidth="0.5" />
                          <circle cx="20" cy="20" r="2" fill="#0190d4" />
                          <path d="M40 20 L25 20" fill="none" stroke="#0190d4" strokeWidth="0.5" />
                          <path d="M20 40 L20 25" fill="none" stroke="#0190d4" strokeWidth="0.5" />
                        </pattern>
                      </defs>
                      <rect width="200" height="200" fill="url(#circuit)" />
                      </svg>
                    </div>
                    
                    <div className="slider-image-container">
                      <img src="/automa.png" alt="Automatización sin fondo en slider" className="slider-image" />

                    </div>
                  </div>
                )}

                {i === 3 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1a252f] via-[#253342] to-[#1a252f]">
                    <div className="absolute inset-0 opacity-40">
                      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none"><defs>
                        <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#0190d4" />
                          <stop offset="100%" stopColor="#017bb5" />
                        </linearGradient>
                      </defs>
                      <circle cx="75" cy="50" r="25" fill="none" stroke="url(#gaugeGrad)" strokeWidth="1" /> <path d="M75 50 L75 30" stroke="#0190d4" strokeWidth="1" /> <circle cx="75" cy="50" r="3" fill="#0190d4" />
                      </svg>
                    </div>
                    <div className="slider-image-container">
                      <img src="/instrume.png" alt="Instrumentación sin fondo en slider" className="slider-image" />

                    </div>
                  </div>
                )}

                <div className="relative h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-2xl">
                      <span className="inline-block px-4 py-1 bg-[#0190d4]/20 text-[#0190d4] rounded-full text-sm font-semibold mb-4">{i===0? 'SOLUCIONES INDUSTRIALES' : i===1? 'CONDUCCIÓN DE FLUIDOS' : i===2? 'CONTROL Y AUTOMATIZACIÓN' : 'MEDICIÓN Y CONTROL'}</span>
                      <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                        {i===0 && 'Soluciones en Válvulas Industriales'}
                        {i===1 && 'Cañerías y Fittings Industriales'}
                        {i===2 && 'Sistemas de Automatización'}
                        {i===3 && 'Instrumentación Industrial'}
                      </h1>
                      <p className="text-xl text-gray-300 mb-8">
                        {i===0 && 'Tecnología y confiabilidad para procesos críticos'}
                        {i===1 && 'Calidad certificada para la industria'}
                        {i===2 && 'Control inteligente para procesos industriales'}
                        {i===3 && 'Precisión y confiabilidad en cada medición'}
                      </p>
                      <button className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-semibold ${i===1? 'bg-white text-[#0190d4]' : 'btn-primary text-white'}`} onClick={(e)=>handleNavClick(e, i===1? '#canerias' : '#contacto')}>
                        {i===1? 'Ver Productos' : i===2? 'Conocer Más' : i===3? 'Descubrir' : 'Solicitar Cotización'}
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
              {[0,1,2,3].map(i => (
                <button key={i} onClick={() => goToSlide(i)} className={`slider-dot w-3 h-3 rounded-full ${i===currentSlide? 'bg-white' : 'bg-white/50'} hover:bg-white transition-colors`} />
              ))}
            </div>

            <button onClick={() => goToSlide(currentSlide-1)} id="prev-slide" className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={() => goToSlide(currentSlide+1)} id="next-slide" className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div className="bg-white py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p id="corporate-text" className="text-lg md:text-xl text-[#3e484a] leading-relaxed mb-8"><strong className="text-[#0190d4]">Dos Vías</strong> es una empresa especializada en soluciones industriales en válvulas, cañerías, fittings, automatización e instrumentación, entregando productos de alta calidad para procesos productivos exigentes.</p>
              <button onClick={(e)=>handleNavClick(e,'#contacto')} className="btn-primary inline-flex items-center px-8 py-4 rounded-lg text-white font-semibold text-lg">Solicitar Cotización <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></button>
            </div>
          </div>
        </section>

        {/* Catalog modal */}
        {catalogOpen && catalogType && productCatalog[catalogType] && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" id="catalog-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8 animate-slide-in">
              <div className="bg-gradient-to-r from-[#0190d4] to-[#017bb5] px-8 py-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-3xl font-bold text-white mb-2">{productCatalog[catalogType].title}</h2>
                    <p className="text-white/90">{productCatalog[catalogType].subtitle}</p>
                  </div>
                  <button onClick={closeCatalog} className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>

              <div className="p-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {productCatalog[catalogType].products.map(product => (
                    <div key={product.id} className="card-hover" onClick={() => showProductDetail(catalogType, product.id)}>
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0190d4] to-[#017bb5] rounded-xl flex items-center justify-center mb-4">
                        {product.iconImg ? (
                          <img src={product.iconImg} alt={`${product.name} icon`} className="product-icon" />
                        ) : (
                          <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" dangerouslySetInnerHTML={{ __html: product.icon }} />
                        )}
                      </div>
                      <h3 className="font-heading text-xl font-bold text-[#3e484a] mb-2">{product.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{product.shortDesc}</p>
                      <div className="flex items-center text-[#0190d4] font-semibold text-sm">Ver detalles
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product detail modal */}
        {currentProduct && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" id="product-detail-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-slide-in">
              <div className="bg-gradient-to-r from-[#3e484a] to-[#2d3436] px-8 py-6 rounded-t-2xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-20 h-20 bg-[#0190d4] rounded-xl flex items-center justify-center flex-shrink-0">
                      {currentProduct.iconImg ? (
                        <img src={currentProduct.iconImg} alt={`${currentProduct.name} icon`} className="product-icon-large" />
                      ) : (
                        <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" dangerouslySetInnerHTML={{ __html: currentProduct.icon }} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h2 className="font-heading text-3xl font-bold text-white mb-2">{currentProduct.name}</h2>
                      <p className="text-white/80 text-lg">{currentProduct.shortDesc}</p>
                    </div>
                  </div>
                  <button onClick={closeProductDetail} className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors ml-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>

              <div className="p-8">
                <div className="mb-8">
                  <h3 className="font-heading text-xl font-bold text-[#3e484a] mb-3 flex items-center"><span className="w-1 h-6 bg-[#0190d4] mr-3"></span>Descripción</h3>
                  <p className="text-gray-700 leading-relaxed text-lg">{currentProduct.description}</p>
                </div>

                <div className="mb-8">
                  <h3 className="font-heading text-xl font-bold text-[#3e484a] mb-4 flex items-center"><span className="w-1 h-6 bg-[#0190d4] mr-3"></span>Características Técnicas</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {currentProduct.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg"><svg className="w-5 h-5 text-[#0190d4] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg><span className="text-gray-700">{feature}</span></div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="font-heading text-xl font-bold text-[#3e484a] mb-4 flex items-center"><span className="w-1 h-6 bg-[#0190d4] mr-3"></span>Aplicaciones</h3>
                  <div className="flex flex-wrap gap-3">{currentProduct.applications.map((app, i) => (<span key={i} className="px-4 py-2 bg-[#0190d4]/10 text-[#0190d4] rounded-lg font-semibold">{app}</span>))}</div>
                </div>

                <div className="bg-gradient-to-r from-[#0190d4]/10 to-[#017bb5]/10 p-6 rounded-xl border-2 border-[#0190d4]/20">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h4 className="font-heading text-lg font-bold text-[#3e484a] mb-1">¿Necesita más información?</h4>
                      <p className="text-gray-600">Contáctenos para cotizaciones y asesoría técnica especializada</p>
                    </div>
                    <a href="#contacto" onClick={() => { closeProductDetail(); closeCatalog(); }} className="btn-primary px-6 py-3 rounded-lg text-white font-semibold flex items-center">Solicitar Cotización <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg></a>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <button onClick={closeProductDetail} className="text-[#0190d4] hover:text-[#017bb5] font-semibold flex items-center mx-auto">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
                    Volver al catálogo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <section id="valvulas" className="py-20 industrial-pattern">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-[#0190d4]/10 text-[#0190d4] rounded-full text-sm font-semibold mb-4">NUESTROS PRODUCTOS</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#3e484a] mb-6">Válvulas Industriales</h2>
              <div className="w-24 h-1 bg-[#0190d4] mx-auto mb-6" />
              <p className="max-w-3xl mx-auto text-gray-600 text-lg">Amplio portafolio de válvulas industriales para alta presión, control de flujo y aplicaciones críticas, disponibles en acero carbono, acero inoxidable, aleaciones especiales, bronce y materiales plásticos industriales.</p>
              <div className="mt-6">
                <button onClick={() => showProductCatalog('valvulas')} className="btn-primary px-8 py-3 rounded-lg text-white font-semibold inline-flex items-center">Ver Catálogo Completo
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {valves.map(({img, title, desc}, idx) => (
                <div key={idx} className="card-hover bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="w-full h-40 mb-4 overflow-hidden rounded-md">
                    <img src={img} alt={title} className="card-img"/>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#3e484a] mb-2">{title}</h3>
                  <p className="text-sm text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="canerias" className="py-20 bg-gradient-to-br from-[#3e484a] to-[#2d3436]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-[#0190d4]/20 text-[#0190d4] rounded-full text-sm font-semibold mb-4">CONDUCCIÓN DE FLUIDOS</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">Cañerías y Fittings</h2>
              <div className="w-24 h-1 bg-[#0190d4] mx-auto mb-6" />
              <p className="max-w-3xl mx-auto text-gray-300 text-lg">Soluciones completas en conducción de fluidos con cañerías y fittings certificados para minería, procesos industriales, energía y plantas productivas.</p>
              <div className="mt-6">
                <button onClick={() => showProductCatalog('canerias')} className="bg-white text-[#0190d4] px-8 py-3 rounded-lg font-semibold inline-flex items-center hover:bg-gray-100 transition-colors">Ver Catálogo Completo
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <img src="/canerias.png" alt="Cañerías" className="canerias-icon" />

                  <h3 className="font-heading text-2xl font-bold text-white">Cañerías</h3>
                </div>
                <p className="text-gray-300 mb-6">Para conducción de fluidos industriales en diversas aplicaciones.</p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-200"><svg className="w-5 h-5 text-[#0190d4] mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> Acero Carbono</li>
                  <li className="flex items-center text-gray-200"><svg className="w-5 h-5 text-[#0190d4] mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> Acero Inoxidable</li>
                  <li className="flex items-center text-gray-200"><svg className="w-5 h-5 text-[#0190d4] mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> Galvanizadas</li>
                  <li className="flex items-center text-gray-200"><svg className="w-5 h-5 text-[#0190d4] mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> API (Oil &amp; Gas)</li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <img src="/fittings.png" alt="Fittings" className="fittings-icon" />
                  <h3 className="font-heading text-2xl font-bold text-white">Fittings</h3>
                </div>
                <p className="text-gray-300 mb-6">Conexiones y accesorios para sistemas de tuberías industriales.</p>
                <div className="grid grid-cols-2 gap-3">
                  {['Codos','Curvas','Tees','Reducciones','Flanges','Coplas','Tapas','Bushings'].map((s,i)=>(
                    <span key={i} className="bg-white/10 px-4 py-2 rounded-lg text-gray-200 text-sm text-center">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="automatizacion" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-[#0190d4]/10 text-[#0190d4] rounded-full text-sm font-semibold mb-4">CONTROL INTELIGENTE</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#3e484a] mb-6">Automatización</h2>
              <div className="w-24 h-1 bg-[#0190d4] mx-auto mb-6" />
              <p className="max-w-3xl mx-auto text-gray-600 text-lg">Sistemas de automatización para operación remota, control de caudal y regulación de procesos industriales.</p>
              <div className="mt-6">
                <button onClick={() => showProductCatalog('automatizacion')} className="btn-primary px-8 py-3 rounded-lg text-white font-semibold inline-flex items-center">Ver Catálogo Completo
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {automationItems.map(([title,desc], idx) => (
                <div key={idx} className="card-hover bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#0190d4] to-[#017bb5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="8" width="12" height="10" rx="2" /></svg>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#3e484a] mb-2">{title}</h3>
                  <p className="text-sm text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="instrumentacion" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-[#0190d4]/10 text-[#0190d4] rounded-full text-sm font-semibold mb-4">MEDICIÓN Y MONITOREO</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#3e484a] mb-6">Instrumentación</h2>
              <div className="w-24 h-1 bg-[#0190d4] mx-auto mb-6" />
              <p className="max-w-3xl mx-auto text-gray-600 text-lg">Instrumentación industrial para medir, monitorear y controlar procesos productivos con precisión y confiabilidad.</p>
              <div className="mt-6">
                <button onClick={() => showProductCatalog('instrumentacion')} className="btn-primary px-8 py-3 rounded-lg text-white font-semibold inline-flex items-center">Ver Catálogo Completo
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {instrumentationItems.map(([title,desc], idx) => (
                <div key={idx} className="card-hover bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-[#0190d4] rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="8" /></svg>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg text-[#3e484a] mb-1">{title}</h3>
                      <p className="text-sm text-gray-500">{desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="representante" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#3e484a] mb-2">Representante de</h2>
              <p className="text-gray-600">Clientes y marcas con las que trabajamos</p>
            </div>

            <div className="partner-carousel">
              <div className="marquee marquee--left">
                <div className="marquee__track" ref={topTrackRef}>
                  {[...partnerLogos, ...partnerLogos].map((src, idx) => (
                    <div className="marquee__item" key={`l-${idx}`}>
                      <img src={src} alt={`Partner ${idx+1}`} className="partner-logo" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="marquee marquee--right" style={{ marginTop: '1rem' }}>
                <div className="marquee__track" ref={bottomTrackRef}>
                  {[...partnerLogosReversed, ...partnerLogosReversed].map((src, idx) => (
                    <div className="marquee__item" key={`r-${idx}`}>
                      <img src={src} alt={`Partner ${idx+1}`} className="partner-logo" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="socio" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/5 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-8 shadow-sm">
              <div className="flex-shrink-0">
                <div className="socio-logo-wrap rounded-lg bg-gradient-to-br from-[#0190d4]/10 to-[#017bb5]/10 p-4">
                  <img src="/logoker.png" alt="Socio clave - Ker" className="socio-logo" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#3e484a] mb-3">Socio Clave</h3>
                <p className="text-gray-600 text-lg leading-relaxed" style={{ textAlign: "justify" }}>Entregamos mantención a tu maquinaria industrial, tanto eléctrica como mecánica. Creamos e implementamos soluciones según la necesidad. Apoyamos y capacitamos a tu equipo para garantizar la continuidad operativa.</p>
                <div className="mt-6">
                  <a href="https://ker.cl" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center px-6 py-3 rounded-lg text-white font-semibold">Visitar Socio
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contacto" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-[#0190d4]/10 text-[#0190d4] rounded-full text-sm font-semibold mb-4">CONTÁCTENOS</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#3e484a] mb-6">Solicite su Cotización</h2>
              <div className="w-24 h-1 bg-[#0190d4] mx-auto mb-6" />
              <p className="max-w-2xl mx-auto text-gray-600 text-lg">Estamos listos para atender sus requerimientos industriales. Complete el formulario y nos contactaremos a la brevedad.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <form id="contact-form" className="bg-white rounded-2xl shadow-xl p-8" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-semibold text-[#3e484a] mb-2">Nombre *</label>
                      <input id="nombre" name="nombre" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#0190d4] transition-colors" placeholder="Su nombre completo" />
                    </div>
                    <div>
                      <label htmlFor="empresa" className="block text-sm font-semibold text-[#3e484a] mb-2">Empresa *</label>
                      <input id="empresa" name="empresa" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#0190d4] transition-colors" placeholder="Nombre de su empresa" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-[#3e484a] mb-2">Correo Electrónico *</label>
                      <input id="email" name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#0190d4] transition-colors" placeholder="correo@empresa.cl" />
                    </div>
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-semibold text-[#3e484a] mb-2">Teléfono</label>
                      <input id="telefono" name="telefono" type="tel" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#0190d4] transition-colors" placeholder="+56 9 1234 5678" />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="mensaje" className="block text-sm font-semibold text-[#3e484a] mb-2">Mensaje *</label>
                    <textarea id="mensaje" name="mensaje" rows="5" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#0190d4] transition-colors resize-none" placeholder="Describa su requerimiento"></textarea>
                  </div>

                  <button type="submit" disabled={submitting} className={`btn-primary w-full py-4 rounded-lg text-white font-semibold text-lg flex items-center justify-center ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {submitting ? 'Enviando...' : 'Enviar Solicitud'}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </button>

                  <div id="form-message" className="mt-4">{formMessage && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg"><div className="flex items-center"><svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg><span><strong>{formMessage.type==='success'?'¡Mensaje enviado!':'Aviso'}</strong> {formMessage.text}</span></div></div>}</div>
                </form>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h3 className="font-heading text-xl font-bold text-[#3e484a] mb-6">Información de Contacto</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#0190d4]/10 rounded-xl flex items-center justify-center flex-shrink-0"><svg className="w-6 h-6 text-[#0190d4]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
                      <div><p className="text-sm text-gray-500 mb-1">Correo Electrónico</p><p id="contact-email" className="font-semibold text-[#3e484a]">contacto@dosvias.cl</p></div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#0190d4]/10 rounded-xl flex items-center justify-center flex-shrink-0"><p>🌐</p></div>
                      <div><p className="text-sm text-gray-500 mb-1">Sitio Web</p><p id="contact-website" className="font-semibold text-[#0190d4]">www.dosvias.cl</p></div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#0190d4] to-[#017bb5] rounded-2xl p-8 text-white">
                  <h3 className="font-heading text-xl font-bold mb-4">¿Por qué elegirnos?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3"><svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span>Productos certificados</span></li>
                    <li className="flex items-start space-x-3"><svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span>Asesoría técnica especializada</span></li>
                    <li className="flex items-start space-x-3"><svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span>Entrega oportuna</span></li>
                    <li className="flex items-start space-x-3"><svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span>Stock disponible</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="bg-[#3e484a] text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="logo-icon-container">
                  <img src="/logo192.png" alt="DOS VÍAS" className="logo-icon-footer" />


                </div>
                
                <p className="text-gray-400 text-sm max-w-md">Especialistas en válvulas, cañerías, fittings, automatización e instrumentación para la industria chilena.</p>
              </div>
              <div><h4 className="font-heading font-bold mb-4">Productos</h4><ul className="space-y-2 text-sm text-gray-400"><li><a href="#valvulas" onClick={(e)=>handleNavClick(e,'#valvulas')} className="hover:text-[#0190d4]">Válvulas</a></li><li><a href="#canerias" onClick={(e)=>handleNavClick(e,'#canerias')} className="hover:text-[#0190d4]">Cañerías y Fittings</a></li><li><a href="#automatizacion" onClick={(e)=>handleNavClick(e,'#automatizacion')} className="hover:text-[#0190d4]">Automatización</a></li><li><a href="#instrumentacion" onClick={(e)=>handleNavClick(e,'#instrumentacion')} className="hover:text-[#0190d4]">Instrumentación</a></li></ul></div>
              <div><h4 className="font-heading font-bold mb-4">Contacto</h4><ul className="space-y-2 text-sm text-gray-400"><li id="footer-email">contacto@dosvias.cl</li><li id="footer-website">www.dosvias.cl</li></ul></div>
            </div>
            <div className="border-t border-gray-600 pt-8 text-center text-sm text-gray-400"><p>© <span id="current-year"></span> DOS VÍAS - Servicios Industriales. Todos los derechos reservados.</p></div>
          </div>
        </footer>
      </main>
    </div>
  );
}
